import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
 
  Users,  CheckCircle, Star, Globe
 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export default function PricingPage() {
  const [activeTab, setActiveTab] = useState('website');
  const [websiteType, setWebsiteType] = useState('static');
  const [pageCount, setPageCount] = useState(5);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const navigate = useNavigate();

  const calculatePrice = () => {
    let basePrice = websiteType === 'static' ? 2500 : 4000;
    let perPagePrice = websiteType === 'static' ? 2200 : 3200;
    let featuresPrice = selectedFeatures.reduce((acc, feature) => acc + feature.price, 0);
    return basePrice + (pageCount * perPagePrice) + featuresPrice;
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <motion.section
        className="relative py-32 bg-gray-800 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="absolute inset-0 opacity-20"
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 100, repeat: Infinity, ease: "linear" }}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="absolute inset-0 border-2 border-blue-600 rounded-full"
              style={{
                transform: `scale(${1 + i * 0.2})`,
              }}
            />
          ))}
        </motion.div>

        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.h1
            className="text-6xl md:text-7xl font-black text-white mb-6"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            Transparent <span className="text-blue-600">Pricing</span>
          </motion.h1>
          <motion.p
            className="text-xl text-gray-300 max-w-2xl mx-auto"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Clear, flexible pricing tailored to your project needs. No hidden costs, just straightforward value.
          </motion.p>
        </div>
      </motion.section>

      {/* Service Tabs */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="flex justify-center mb-12">
            <div className="bg-gray-100 p-2 rounded-xl">
              {['website', 'mobile', 'custom'].map((tab) => (
                <motion.button
                  key={tab}
                  className={`px-8 py-3 rounded-lg font-medium ${activeTab === tab
                    ? 'bg-white text-indigo-700'
                    : 'text-gray-600 hover:text-black'
                    }`}
                  onClick={() => setActiveTab(tab)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Website Development Pricing */}
          {activeTab === 'website' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-12"
            >
              {/* Package Tiers */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {websitePackages.map((pkg, index) => (
                  <motion.div
                    key={index}
                    className={`rounded-2xl p-8 ${pkg.featured
                      ? 'bg-white text-indigo-700'
                      : 'bg-white border-2 border-gray-200'
                      }`}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                    whileHover={{ y: -10 }}
                  >
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                        <p className={pkg.featured ? 'text-blue-400' : 'text-blue-600'}>
                          {pkg.description}
                        </p>
                      </div>
                      {pkg.featured && (
                        <span className="bg-blue-600 text-black text-sm font-bold px-3 py-1 rounded-full">
                          Popular
                        </span>
                      )}
                    </div>
                    <div className="mb-6">
                      <span className="text-4xl font-bold">₹{pkg.price}</span>
                      <span className={pkg.featured ? 'text-blue-300' : 'text-blue-600'}>
                        /project
                      </span>
                    </div>
                    <ul className="space-y-4 mb-8">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center">
                          <span className="text-blue-600 mr-2">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <motion.button
                      className={`w-full py-3 rounded-lg font-medium ${pkg.featured
                        ? 'bg-blue-600 text-black hover:bg-indigo-500'
                        : 'bg-gray-800 text-white hover:bg-gray-900'
                        }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate('/contact')}

                    >
                      Get Started
                    </motion.button>
                  </motion.div>
                ))}
              </div>

              {/* Custom Calculator */}
              <motion.div
                className="bg-gray-50 rounded-2xl p-8 mt-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <h3 className="text-2xl font-bold mb-6">Custom Website Calculator</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <div className="mb-6">
                      <label className="block text-gray-700 font-medium mb-2">
                        Website Type
                      </label>
                      <select
                        className="w-full px-4 py-3 rounded-lg border border-gray-300"
                        value={websiteType}
                        onChange={(e) => setWebsiteType(e.target.value)}
                      >
                        <option value="static">Static Website</option>
                        <option value="dynamic">Dynamic Website</option>
                      </select>
                    </div>
                    <div className="mb-6">
                      <label className="block text-gray-700 font-medium mb-2">
                        Number of Pages
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={pageCount}
                        onChange={(e) => setPageCount(parseInt(e.target.value))}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Additional Features
                    </label>
                    <div className="space-y-3">
                      {additionalFeatures.map((feature, index) => (
                        <label key={index} className="flex items-center">
                          <input
                            type="checkbox"
                            className="form-checkbox text-blue-600 rounded"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedFeatures([...selectedFeatures, feature]);
                              } else {
                                setSelectedFeatures(
                                  selectedFeatures.filter(f => f.id !== feature.id)
                                );
                              }
                            }}
                          />
                          <span className="ml-2">{feature.name} (+₹{feature.price})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-8 p-6 bg-white text-indigo-700 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="text-xl font-bold">Estimated Total</h4>
                      <p className="text-gray-400">Based on your selections</p>
                    </div>
                    <div className="text-3xl font-bold">
                    ₹{calculatePrice().toLocaleString()}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Mobile App Development Pricing */}
          {activeTab === 'mobile' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {mobilePackages.map((pkg, index) => (
                <motion.div
                  key={index}
                  className={`rounded-2xl p-8 ${pkg.featured ? 'bg-white text-indigo-700' : 'bg-white border-2 border-gray-200'
                    }`}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  whileHover={{ y: -10 }}
                >
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                      <p className={pkg.featured ? 'text-blue-600' : 'text-blue-500'}>
                        {pkg.description}
                      </p>
                    </div>
                  </div>
                  <div className="mb-6">
                    <span className="text-4xl font-bold">₹{pkg.price}</span>
                    <span className={pkg.featured ? 'text-gray-300' : 'text-gray-600'}>
                      /project
                    </span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <span className="text-blue-600 mr-2">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <motion.button
                    className={`w-full py-3 rounded-lg font-medium ${pkg.featured
                      ? 'bg-blue-600 text-black hover:bg-yellow-500'
                      : 'bg-white text-indigo-700 hover:bg-gray-900'
                      }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/contact')}
                  >
                    Get Started
                  </motion.button>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Custom Solutions */}
          {activeTab === 'custom' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto text-center"
            >
              <motion.div
                className="bg-white text-indigo-700 rounded-2xl p-12"
                whileHover={{ scale: 1.02 }}
              >
                <h3 className="text-3xl font-bold mb-4">Custom Enterprise Solutions</h3>
                <p className="text-gray-300 mb-8">
                  Let's build something unique together. Our team will work with you to understand
                  your specific requirements and create a tailored solution.
                </p>
                <motion.button
                  className="bg-blue-600 text-black px-8 py-3 rounded-lg font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/contact')}
                >
                  Schedule Consultation
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Project Metrics Section */}
      <motion.section className="py-20 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {projectMetrics.map((metric, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex justify-center text-blue-600 mb-4">{metric.icon}</div>
                <h3 className="text-4xl font-bold mb-2">{metric.number}</h3>
                <p className="text-gray-400">{metric.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto">
            {pricingFaqs.map((faq, index) => (
              <PricingFAQ key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// Components
const PricingFAQ = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      className="border-b border-gray-200 py-6"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      <button
        className="w-full flex justify-between items-center text-left"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-lg font-medium">{question}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          ↓
        </motion.span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="text-gray-600 mt-4">{answer}</p>
      </motion.div>
    </motion.div>
  );
};

// Data
// Project metrics
const projectMetrics = [
  { number: "100+", label: "Projects Delivered", icon: <CheckCircle className="w-6 h-6" /> },
  { number: "99%", label: "Client Satisfaction", icon: <Star className="w-6 h-6" /> },
  { number: "5+", label: "Countries Served", icon: <Globe className="w-6 h-6" /> },
  { number: "50+", label: "Expert Developers", icon: <Users className="w-6 h-6" /> }
];
// Data (continued)
const websitePackages = [
  {
    name: "Basic",
    description: "Perfect for small businesses",
    price: "15,999",
    features: [
      "Up to 5 Pages",
      "Responsive Design",
      "Basic SEO Setup",
      "Mobile-Friendly",
      "3 Rounds of Revisions",
      "Static Website",
      "Custom Forms",
      "Hosting Included",
      "Maintenance Excluded",
    ]
  },
  {
    name: "Professional",
    description: "Ideal for growing businesses",
    price: "89,999",
    featured: true,
    features: [
      "Up to 10 Pages",
      "Advanced SEO Optimization",
      "Content Management System",
      "E-commerce Integration",
      "Custom Forms",
      "Analytics Integration",
      "Social Media Integration",
      "5 Rounds of Revisions",
      "Backend Support",
      "Hosting Included",
      "Maintenance Excluded",
    ]
  },
  {
    name: "Enterprise",
    description: "For large organizations",
    price: "1,99,999",
    features: [
      "Included Professional Services",
      "Unlimited Pages",
      "Custom Functionality",
      "Advanced Security",
      "Priority Support",
      "Performance Optimization",
      "Custom Integrations",
      "Unlimited Revisions",
      "24/7 Support"
    ]
  }
];

const mobilePackages = [
  {
    name: "Starter App",
    description: "Basic mobile application",
    price: "25,999",
    features: [
      "Single Platform (iOS or Android)",
      "Basic Features",
      "User Authentication",
      "Push Notifications",
      "Basic Analytics",
      "3 Months Support"
    ]
  },
  {
    name: "Professional App",
    description: "Full-featured application",
    price: "95,999",
    featured: true,
    features: [
      "Cross-Platform (iOS & Android)",
      "Advanced Features",
      "Custom UI/UX Design",
      "API Integration",
      "Advanced Analytics",
      "Offline Support",
      "6 Months Support"
    ]
  },
  {
    name: "Enterprise App",
    description: "Custom enterprise solution",
    price: "2,99,999",
    features: [
      "Cross-Platform with Web Admin",
      "Custom Enterprise Features",
      "Third-party Integrations",
      "Advanced Security",
      "Scalable Architecture",
      "White Label Solution",
      "1 Year Support"
    ]
  }
];

const additionalFeatures = [
  {
    id: 1,
    name: "E-commerce Functionality",
    price: 1500
  },
  {
    id: 2,
    name: "Custom API Integration",
    price: 1000
  },
  {
    id: 3,
    name: "Advanced Security Features",
    price: 800
  },
  {
    id: 4,
    name: "Multi-language Support",
    price: 600
  },
  {
    id: 5,
    name: "Advanced Analytics",
    price: 500
  }
];

const pricingFaqs = [
  {
    question: "How long does it take to complete a project?",
    answer: "Project timelines vary based on complexity. A basic website typically takes 4-6 weeks, while complex applications may take 3-6 months. We'll provide a detailed timeline during our initial consultation."
  },
  {
    question: "Do you offer maintenance and support?",
    answer: "Yes, we offer various maintenance and support packages. Each project includes a basic support period, and we can create custom maintenance plans based on your needs."
  },
  {
    question: "What is your payment structure?",
    answer: "We typically require a 50% upfront deposit to begin work, with the remaining balance due upon project completion. For larger projects, we can arrange milestone-based payments."
  },
  {
    question: "Can I update the website myself?",
    answer: "Yes, we build our websites with user-friendly content management systems that allow you to make basic updates. We also provide training and documentation."
  },
  {
    question: "Do you offer hosting services?",
    answer: "Yes, we provide reliable hosting solutions with 99.9% uptime guarantee. We can also work with your existing hosting provider if preferred."
  }
];

// export default PricingPage;